/**
 * SEO Component
 *
 * Handles per-page meta tags, Open Graph tags, Twitter cards, canonical URLs,
 * and JSON-LD structured data.
 *
 * Site-wide structured data (LocalBusiness, Organization, WebSite) lives
 * statically in `index.html` so search engines index it without running JS.
 * This component adds *page-specific* structured data on top of that.
 *
 * Usage:
 *   <SEO
 *     title="About Us"
 *     description="..."
 *     path="/about"            // canonical path; required for all non-home pages
 *     image="/og-about.jpg"    // optional; falls back to default og-image
 *     type="website"           // or "article" for blog posts
 *     structuredData={[{...}]} // optional array of JSON-LD objects
 *     noIndex={false}
 *   />
 */

import { Helmet } from 'react-helmet-async'
import { SITE_URL } from '../../data/site'
import { optimizedUrl } from '../../utils/images'

const DEFAULT_IMAGE = '/og-image.jpg'
const DEFAULT_TITLE = 'Recreo Rehab — Massage & Athletic Therapy at Yonge & Eglinton'
const DEFAULT_DESCRIPTION =
  'Registered massage therapy, athletic therapy, naturopathic care, and acupuncture at Yonge & Eglinton, Midtown Toronto.'
const SITE_NAME = 'Recreo Rehab'

const absoluteUrl = (pathOrUrl) => {
  if (!pathOrUrl) return null
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
  const clean = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`
  return `${SITE_URL}${clean}`
}

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  imageAlt = 'Recreo Rehab — Massage & Athletic Therapy at Yonge & Eglinton, Toronto',
  keywords,
  type = 'website',
  noIndex = false,
  structuredData = [],
  // article-specific (for blog posts later)
  publishedTime,
  modifiedTime,
  author,
}) => {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : DEFAULT_TITLE

  const canonical = absoluteUrl(path)
  const ogSrc = image === DEFAULT_IMAGE ? image : optimizedUrl(image, 1100, 'webp')
  const absImage = absoluteUrl(ogSrc)
  const imageType = ogSrc.endsWith('.webp') ? 'image/webp' : 'image/jpeg'

  const robots = noIndex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1'

  // Normalize structured data to array
  const structuredBlocks = Array.isArray(structuredData)
    ? structuredData
    : [structuredData].filter(Boolean)

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_CA" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title || DEFAULT_TITLE} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absImage} />
      <meta property="og:image:secure_url" content={absImage} />
      <meta property="og:image:type" content={imageType} />
      {ogSrc === DEFAULT_IMAGE ? <meta property="og:image:width" content="1200" /> : null}
      {ogSrc === DEFAULT_IMAGE ? <meta property="og:image:height" content="630" /> : null}
      <meta property="og:image:alt" content={imageAlt} />

      {/* Article-specific (blog) */}
      {type === 'article' && publishedTime ? (
        <meta property="article:published_time" content={publishedTime} />
      ) : null}
      {type === 'article' && modifiedTime ? (
        <meta property="article:modified_time" content={modifiedTime} />
      ) : null}
      {type === 'article' && author ? (
        <meta property="article:author" content={author} />
      ) : null}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title || DEFAULT_TITLE} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Page-specific structured data (LocalBusiness/Organization live in index.html) */}
      {structuredBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  )
}

/**
 * Helper: build a BreadcrumbList JSON-LD object.
 *
 * Example:
 *   breadcrumbs([
 *     { name: 'Home', path: '/' },
 *     { name: 'Services', path: '/services' },
 *   ])
 */
export const breadcrumbs = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
})

export default SEO
